import React, { useState, useEffect } from "react";
import ModalTemplate from "../../common/components/ModalTemplate";
import PropTypes from "prop-types";
import Button from "../../common/components/Button";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

const CheckItem = ({ title, status, error }) => {
  return (
    <div className="flex items-center gap-2 mb-2">
      {title}
      {status === "loading" && <LoadingSpinner />}
      {status === "success" && (
        <CheckCircleIcon className="text-successGreen w-8" />
      )}
      {status === "error" && (
        <XMarkIcon className="text-red-500 w-8 font-sans" />
      )}
      <span>
        {status === "error" && (
          <span className="text-red-500 ml-1">{error}</span>
        )}
      </span>
    </div>
  );
};

CheckItem.propTypes = {
  title: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  error: PropTypes.string,
};

const TechCheckModal = ({ isOpen, onClose, managerRef }) => {
  const [compatibilityStatus, setCompatibilityStatus] = useState("loading");
  const [permissionsStatus, setPermissionsStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const checkCompatibility = async () => {
      try {
        await managerRef.current.checkCompatibility();
        setCompatibilityStatus("success");
      } catch (error) {
        setCompatibilityStatus("error");
        setErrors((prev) => ({ ...prev, compatibility: error.message }));
      }
    };

    if (isOpen && !sessionStorage.getItem("hasCompletedTechCheck")) {
      checkCompatibility();
    }
  }, [isOpen, managerRef]);

  const handleGetPermissions = async () => {
    setPermissionsStatus("loading");
    try {
      // This will prompt for permissions without activating devices
      const devices = await navigator.mediaDevices.enumerateDevices();

      // Check if we have both video and audio permissions
      const hasVideoPermission = devices.some(
        (device) => device.kind === "videoinput" && device.label !== ""
      );
      const hasAudioPermission = devices.some(
        (device) => device.kind === "audioinput" && device.label !== ""
      );

      if (!hasVideoPermission || !hasAudioPermission) {
        // If we don't have permissions, we need to request them
        // This will activate the camera temporarily
        await navigator.mediaDevices
          .getUserMedia({ video: true, audio: true })
          .then((stream) => {
            // Immediately stop all tracks
            stream.getTracks().forEach((track) => track.stop());
          });
      }

      setPermissionsStatus("success");
      await new Promise((resolve) => setTimeout(resolve, 500));
      onClose("success");
    } catch (error) {
      setPermissionsStatus("error");
      setErrors((prev) => ({ ...prev, permissions: error.message }));
    }
  };

  return (
    <ModalTemplate
      isOpen={isOpen}
      onClose={onClose}
      title="Compatibility Check"
      subtitle="First, we need to ensure your device is ready to record."
      showCloseButton={false}
      maxWidth="lg"
    >
      <div className="p-3">
        <CheckItem
          title="Checking device compatibility: "
          status={compatibilityStatus}
          error={errors.compatibility}
        />

        {compatibilityStatus === "success" && (
          <div className="flex flex-row gap-2">
            <CheckItem
              title="Grant camera and microphone permissions:"
              status={permissionsStatus}
              error={errors.permissions}
            />
            {permissionsStatus === "idle" && (
              <Button
                text="Continue"
                color="primary"
                onClick={handleGetPermissions}
                disabled={permissionsStatus === "loading"}
              />
            )}
          </div>
        )}
      </div>
    </ModalTemplate>
  );
};

TechCheckModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  managerRef: PropTypes.object.isRequired,
};

export default TechCheckModal;
